import { useLocation, useNavigate } from "react-router-dom";
import { verticleDot } from "../../asset";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import EmptyTable from "../../components/EmptyTable";
import PageHeader from "../../components/PageHeader";
import Tag from "../../components/Tags";
import {
  formatDateTime,
  popupNavigation,
} from "../../util/functions/UniversalFunctions";
import { useEffect, useState } from "react";
import SelectClassModal from "../../components/modal/SelectClassModel";
import { AppDispatch } from "../../store";
import { useDispatch } from "react-redux";
import { setSelectedClassIds } from "../../features/ptm/PtmSlice";
import schedulePtmApis from "../../api/schedulePtm";
import { PtmResponse } from "../../types/SchedulePtm";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import { useBranchCode } from "../../util/hooks/useBranch";

export default function PtmList() {
  const navigate = useNavigate();
  const [showSelectClass, setShowSelectClass] = useState(false);
  const [ptms, setPtms] = useState<PtmResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch<AppDispatch>();
  const { confirm, modal } = useConfirmToast();
  const location = useLocation();
  const branchCode = useBranchCode();
  const [editPtm, setEditPtm] = useState<PtmResponse | null>(null);

  useEffect(() => {
    const fetchPtms = async () => {
      setLoading(true);
      const data = await schedulePtmApis.getPtmsByBranch(branchCode);
      setPtms(data);
      setLoading(false);
    };

    fetchPtms();
  }, [location.state?.refetch, branchCode]);

  const handleCancelPtm = async (ptmId: number) => {
    const confirmed = await confirm(
      "Are you sure you want to cancel this PTM?",
      {
        confirmText: "Yes, Cancel",
      },
    );
    if (!confirmed) return;
    try {
      await schedulePtmApis.canclePtm(ptmId);
      // UI update
      setPtms((prev) => prev.filter((p) => p.ptmId !== ptmId));
    } catch {
      // toast already handled globally
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <PageHeader />
        <CustomButton
          color="bg-primary-500"
          text="Schedule New PTM"
          onClick={() => {
            setEditPtm(null);
            setShowSelectClass(true);
          }}
        />
        {showSelectClass && (
          <SelectClassModal
            title={
              editPtm
                ? "Update PTM - Select Classes"
                : "Select Class to Schedule PTM"
            }
            actionLabel="Next"
            defaultSelectedIds={editPtm?.classIds}
            // isEdit={!!editPtm}
            // ptm={editPtm}
            onAction={(selectedClasses) => {
              const classIds = selectedClasses?.map((c) => c.classId); 

              dispatch(setSelectedClassIds(classIds));
              setShowSelectClass(false);

              navigate(popupNavigation("selectPtmDateTime"), {
                state: {
                  ptm: editPtm,
                  isEdit: !!editPtm,
                  selectedClassIds: classIds,
                  refetch: true,
                },
              });
            }}
            onClose={() => {
              setShowSelectClass(false);
              setEditPtm(null);
            }}
          />
        )}
      </div>

      <div className="bg-white text-neutral-400 flex flex-col gap-9 rounded-lg px-6 py-5">
        {ptms.length > 0 ? (
          ptms?.map((item, index) => (
            <div key={index} className="grid md:grid-cols-2 gap-10 py-4">
              <div className="lg:relative flex flex-col gap-2 w-full">
                <CustomInput
                  label="PTM Scheduled For Date/Time"
                  name=""
                  readOnly
                  value={formatDateTime(item.scheduleDateTime)}
                  onChange={() => {}}
                  placeholder="Schedule PTM"
                />
                <div className="lg:absolute flex justify-center items-center right-2 lg:bottom-2">
                  <button
                    className="bg-warning-700 text-white text-[13px] font-medium py-1 px-4 rounded"
                    onClick={() =>
                      navigate(popupNavigation("selectPtmDateTime"), {
                        state: { ptm: item, isEdit: true, refetch: true },
                      })
                    }
                  >
                    Update
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-semibold text-neutral-400">
                    PTM Scheduled For Classes
                  </label>

                  <div className="relative group">
                    <img src={verticleDot} alt="" className="cursor-pointer" />

                    <div
                      className="
      absolute right-0 mt-2 max-w-52 p-2 bg-white rounded-lg shadow-xl z-20
      opacity-0 invisible group-hover:opacity-100 group-hover:visible
      transition-all duration-200
    "
                    >
                      <CustomButton
                        color="bg-error-700"
                        text="Cancel PTM"
                        onClick={() => handleCancelPtm(item.ptmId)}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {item.classes && item.classes.length > 0 ? (
                    item.classes.map((c) => (
                      <Tag
                        key={c.classId}
                        variant="solid"
                        text={c.className}
                        color="primary"
                        size="sm"
                        padding="wide"
                      />
                    ))
                  ) : (
                    <Tag
                      variant="solid"
                      text="---"
                      color="neutral"
                      size="sm"
                      padding="wide"
                    />
                  )}
                </div>
              </div>

              <div className="flex gap-5 items-center">
                <label className="text-sm font-semibold text-neutral-400">
                  PTM Teacher In-charge
                </label>
                <Tag
                  variant="solid"
                  text={item.teacherInChargeName ?? "----"}
                  color="success"
                  size="sm"
                  padding="wide"
                />
              </div>

              <div className="flex gap-5 items-center">
                <label className="text-sm font-semibold text-neutral-400">
                  PTM Coordinator
                </label>
                <Tag
                  variant="solid"
                  text={item.coordinatorName ?? "----"}
                  color="success"
                  size="sm"
                  padding="wide"
                />
              </div>
            </div>
          ))
        ) : (
          <EmptyTable
            customButton={
              <CustomButton
                color="bg-primary-500"
                text="Schedule New PTM"
                onClick={() => setShowSelectClass(true)}
              />
            }
          />
        )}
      </div>
      {modal}
    </div>
  );
}
